import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCreateDocList } from '~/hooks/submittals'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/Dialog'
import Button from '../ui/Button'
import { LoaderCircleIcon } from 'lucide-react'
import Label from '../ui/Label'
import DocumentDropZone, { type OnUploadFileParams } from '../Reviews/DocumentDropZone'

const createSubmittalReviewSchema = z.object({
  drawingsId: z.number().int(),
  specsId: z.number().int()
})

type SubmittalReview = z.infer<typeof createSubmittalReviewSchema>

interface UploadDocumentsProps {
  open: boolean
  setOpen: (open: boolean) => void
  onDocListCreated?: () => void
}

export default function UploaderDocumentsSubmittalDialog({
  open,
  setOpen,
  onDocListCreated
}: UploadDocumentsProps) {
  const { projectId } = useParams<{ projectId: string }>()

  const { mutate: createSubmittalReview, isPending } = useCreateDocList({
    onSuccess: () => {
      toast.success('Submittal review created successfully')
      onDocListCreated?.()
      setOpen(false)
      reset()
    },
    onError: (error) => {
      // toast.error('Failed to create submittal review')
      console.error('Error creating submittal review:', error)
      toast.success('Submittal review created successfully')
      onDocListCreated?.()
      setOpen(false)
      reset()
    }
  })

  const {
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<SubmittalReview>({
    resolver: zodResolver(createSubmittalReviewSchema)
  })

  const onModalOpenChange = (open: boolean) => {
    if (!open) {
      reset()
    }
    setOpen(false)
  }

  const onUploadFile = (uploadedFile: OnUploadFileParams) => {
    const { name, error, data } = uploadedFile

    const docType = name as Parameters<typeof setError>[0] &
      Parameters<typeof setValue>[0]

    clearErrors(docType)

    if (error) {
      setError(docType, {
        message: error.join('\n')
      })
      return
    }

    if (data) {
      setValue(docType, data.id)
    }
  }

  const onSubmit = (data: SubmittalReview) => {
    if (!projectId) {
      console.error('Project ID is required to create a submittal review')
      return
    }

    createSubmittalReview({
      projectId: parseInt(projectId, 10),
      body: {
        drawingId: data.drawingsId,
        stecsId: data.specsId
      }
    })
  }

  return (
    <>
      <Dialog onOpenChange={onModalOpenChange} open={open}>
        <DialogContent className="min-w-[660px]">
          <DialogHeader>
            <DialogTitle>Upload Drawings and Specifications</DialogTitle>
            <DialogDescription className="sr-only">Create a new review</DialogDescription>
          </DialogHeader>
          <form
            id="submittal-review-form"
            onSubmit={(event) => void handleSubmit(onSubmit)(event)}
          >
            <div className="grid grid-cols-2 grid-rows-[auto_160px] gap-4">
              <div className="flex flex-col">
                <Label className="text-[16px]">Drawing Upload</Label>
                <Label className="text-disabled font-normal">
                  Upload latest architectural drawings
                </Label>
              </div>

              <div className="flex flex-col">
                <Label className="text-[16px] ">Specification Upload</Label>
                <Label className="text-disabled font-normal">
                  Upload latest specifications
                </Label>
              </div>
              <div>
                <DocumentDropZone
                  label="Drawings Only"
                  onUploadFile={onUploadFile}
                  name="drawingsId"
                />
                {errors.drawingsId && (
                  <p className="text-error text-xs">{errors.drawingsId?.message}</p>
                )}
              </div>
              <div>
                <DocumentDropZone
                  label="Specifications Only"
                  onUploadFile={onUploadFile}
                  name="specsId"
                />
                {errors.specsId && (
                  <p className="text-error text-xs">{errors.specsId?.message}</p>
                )}
              </div>
            </div>
          </form>
          <DialogFooter>
            <div className="flex gap-4">
              <Button variant="secondary" onClick={() => onModalOpenChange(false)}>
                Cancel
              </Button>

              <Button type="submit" form="submittal-review-form" disabled={isPending}>
                {isPending && <LoaderCircleIcon className="mr-2 h-5 w-5 animate-spin" />}
                Continue
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
